/**
 * メール配信の画面の日本語（en.mail.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。
 * 表記の決め事は ja.ts の冒頭と同じ。法律の説明はやさしく 1〜2 行（指示書 mail-ui.md の 2）。
 */
import type { enMail } from './en.mail';
import type { PluralMsg } from './index';

type MailMessages = { [K in keyof typeof enMail]: (typeof enMail)[K] extends string ? string : string | PluralMsg };

export const jaMail: MailMessages = {
  'nav.mail': 'メール',
  'mail.title': 'メール',
  'mail.lead': 'このアプリのメールを希望した利用者に、お知らせを送れます。届くのは、受け取りに同意してメールで確認を済ませた人だけで、どのメールにも配信停止のリンクが付きます。',
  'mail.statusWhat': 'メールの状態',

  'mail.kids.title': '子ども向けのアプリではメールを使えません',
  'mail.kids.body': 'このプロジェクトは{key}です。メールは大人の利用者にだけ送れるため、このプロジェクトではONにできません。',
  'mail.off.title': '{env}ではメールがOFFです',
  'mail.off.body': 'メールは最初はOFFです。ONにすると:',
  'mail.off.point.consent': '利用者がアプリの中で受け取りに同意し、メールで確認します。同意した人がいないうちは、送る相手は0人です。',
  'mail.off.point.adults': '届くのは大人の利用者だけです。',
  'mail.off.point.sender': '正式な氏名（または法人名）・住所・問い合わせ先を一度だけ入れます。法律でメールの最後に必ず書くことになっています。',
  'mail.off.how': '手元のファイルでONにして反映するか、{link}で切り替えてください。',
  'mail.off.kidsNote': '年齢層（audience）が "kids" のプロジェクトではメールを使えません。',
  'mail.mixedNote': 'このプロジェクトの年齢層は "mixed" です。大人と確認できていない利用者には送られません。',

  'mail.suspended.title': 'メールの送信を止めています',
  'mail.suspended.reason': '理由: {reason}',
  'mail.suspended.reason.complaint_rate': '迷惑メールとして報告された割合が多い（0.1%以上）',
  'mail.suspended.reason.bounce_rate': '存在しない宛先の割合が多い（5%以上）',
  'mail.suspended.reason.unknown': '{product}の運営が止めています',
  'mail.suspended.since': '{at}から',
  'mail.suspended.body': '利用者と、メールを送るほかのプロジェクトを守るための停止です。再開には{product}の運営の確認が要ります（サポートに確認を依頼してください）。予約・定期のメールは送信が止まったときに取り消されました。再開しても戻らないので、作り直してください。',

  'mail.sender.title': '差出人情報',
  'mail.sender.why': '宣伝のメールには、送った人と実在する住所を書くことが法律で決まっています。ここに入れた内容はメールの最後に自動で入り、利用者に見えます。',
  'mail.sender.shared': 'あなたのすべてのプロジェクトで共通です。住所は私書箱でもかまいません。',
  'mail.sender.complete': '入力済み',
  'mail.sender.incomplete': '送る前に必要',
  'mail.sender.legalName': '正式な氏名',
  'mail.sender.legalNameHint': '本名、または法人名',
  'mail.sender.postalAddress': '住所',
  'mail.sender.postalAddressHint': '私書箱でも可',
  'mail.sender.contactEmail': '問い合わせ先のメール',
  'mail.sender.contactEmailHint': '利用者からの連絡先',
  'mail.sender.save': '差出人情報を保存',
  'mail.sender.saved': '差出人情報を保存しました。',
  'mail.sender.edit': '変更',

  'mail.problem.required': '入力してください。',
  'mail.problem.tooLong': '長すぎます（{max}文字まで）。',
  'mail.problem.email': 'name@example.com のような形のメールアドレスを入れてください。',
  'mail.problem.newline': '件名は1行で書いてください。',
  'mail.problem.invalidTime': 'その日時はありません（2月30日や、夏時間で飛ばされる時刻など）。',
  'mail.problem.past': '今から5分より後の時刻を選んでください。',
  'mail.problem.tooFar': '1年以内の時刻を選んでください。',
  'mail.problem.monthDay': '毎月の場合、最初の回はUTCで1日〜28日になる日時を選んでください（29〜31日が無い月があるため）。日本時間では、1日の朝9時より前はUTCで前の月の末日になります。',

  'mail.compose.title': 'メールを書く',
  'mail.compose.subject': '件名',
  'mail.compose.text': '本文',
  'mail.compose.textHint': 'テキスト',
  'mail.compose.zero': '送る相手がまだいません。',
  'mail.compose.submit.now': '確認して送る',
  'mail.compose.submit.schedule': '確認して予約',
  'mail.compose.submit.recurring': '確認して設定',
  'mail.compose.blocked.sender': '先に{link}を入れてください。3つとも揃うまで送れません。',
  'mail.compose.blocked.suspended': '送信を止めています（上の説明）。書くことはできますが、運営が再開するまで送れません。',

  'mail.preview.title': '見本',
  'mail.preview.from': '差出人',
  'mail.preview.subject': '件名',
  'mail.preview.noSubject': '（件名はまだありません）',
  'mail.preview.noText': '（ここに本文が入ります）',
  'mail.preview.footer': '自動で付くフッター',
  'mail.preview.missing.legalName': '（正式な氏名が未入力）',
  'mail.preview.missing.postalAddress': '（住所が未入力）',
  'mail.preview.missing.contactEmail': '（問い合わせ先が未入力）',
  'mail.preview.unsubscribeUrl': '利用者ごとの配信停止のリンク',
  'mail.preview.domainPending': 'アプリの送信用アドレス',
  'mail.preview.note': 'フッターはすべてのメールに付き、消せません。利用者ごとに、その人の言語（日本語か英語）で入ります。',
  // フッターの全文（Core の compose.ts の footerLines の ja と一字一句同じ。表記の決め事より実物との一致を優先）
  'mail.footer.notice': 'このメールは、{game} のお知らせの受け取りに登録した方にお送りしている広告・宣伝のメールです。',
  'mail.footer.sender': '送信者: {value}',
  'mail.footer.address': '住所: {value}',
  'mail.footer.contact': '問い合わせ先: {value}',
  'mail.footer.unsubscribe': '配信停止: {url}',

  'mail.audience.title': '宛先',
  'mail.audience.language': '利用者の言語',
  'mail.audience.allLanguages': 'すべての言語',
  'mail.audience.lastPlayed': '最後に遊んだ日',
  'mail.audience.anyTime': 'いつでも',
  'mail.audience.withinDays': '{count}日以内',
  'mail.audience.counting': '宛先を数えています…',
  'mail.audience.unknown': '今は宛先の数を数えられませんでした。',
  'mail.audience.count': '{n}人に届きます',
  'mail.audience.who': '受け取りに同意して確認を済ませた、大人の、配信を止めていない利用者だけです。BAN 中の利用者には送りません（同意は残ります）。メールアドレスはあなたにも表示されません。',
  'mail.lang.ja': '日本語',
  'mail.lang.en': '英語',

  'mail.when.title': '送るとき',
  'mail.when.mode.now': '今すぐ',
  'mail.when.mode.schedule': '予約',
  'mail.when.mode.recurring': '定期',
  'mail.when.yourTime': 'あなたの時刻で',
  'mail.when.date': '日付',
  'mail.when.firstDate': '最初の回',
  'mail.when.time': '時刻',
  'mail.when.every': 'くり返し',
  'mail.every.day': '毎日',
  'mail.every.week': '毎週',
  'mail.every.month': '毎月',
  'mail.everyLabel.day': '毎日 {time}',
  'mail.everyLabel.week': '毎週{weekday} {time}',
  'mail.everyLabel.month': '毎月{day}日 {time}',
  'mail.when.recurringHint.day': '最初の回から、毎日この時刻に送ります。',
  'mail.when.recurringHint.week': '最初の回と同じ曜日に、毎週送ります。',
  'mail.when.recurringHint.month': '最初の回と同じ日に、毎月送ります。',
  'mail.when.recurringRule': '1回目は最初の回の日時ちょうどに、2回目からは同じ時刻（UTCで固定）に送ります。止まっていた間の回は、1日より前のものは後から送らずに飛ばします。',
  'mail.when.dstNote': '定期のメールはUTCの時刻で固定されるため、夏時間の切り替えで、あなたの時刻では1時間ずれます。',
  'mail.when.now': 'すぐに',
  'mail.when.recurring': '{every}（最初: {first}）',

  'mail.confirm.title.now': 'このメールを今すぐ送りますか？',
  'mail.confirm.title.schedule': 'このメールを予約しますか？',
  'mail.confirm.title.recurring': 'この定期のメールを設定しますか？',
  'mail.confirm.go.now': '今すぐ送る',
  'mail.confirm.go.schedule': '予約する',
  'mail.confirm.go.recurring': '定期で送る',
  'mail.confirm.recipients': '宛先',
  'mail.confirm.recipientsN': '{count}人',
  'mail.confirm.recipientsUnknown': '不明（送るときに数えます）',
  'mail.confirm.subject': '件名',
  'mail.confirm.when': '送る時刻',
  'mail.confirm.noUndo': '届いたメールは取り消せません。',
  'mail.confirm.cancelLater': '送られるまでは、下の履歴から取り消せます。宛先は送るときに数え直します。',
  'mail.confirm.cost': '送ったメール1通ごとに、原価が前払い残高から引かれます。',
  'mail.confirm.recurringBalance': '前払い残高が尽きると、この定期の配信は取り消され、入金しても再開しません（作り直しになります）。',
  'mail.done.now': '送信を始めました。結果は下の履歴に順に出ます。',
  'mail.done.scheduled': '{at}（あなたの時刻）に予約しました。',

  'mail.warmup.title': '送り始めの1日の上限',
  'mail.warmup.day': '{day}日目 / {days}日',
  'mail.warmup.body': '送り始めて{days}日間は、1日に送れる数に上限があります（今日{limit}通・残り{remaining}通）。超えた分は翌日から自動で続けて送ります。',
  'mail.warmup.notStarted': '最初に送ってから{days}日間は、1日に送れる数に上限があります（1日目は{limit}通）。超えた分は翌日から自動で続けて送ります。',
  'mail.warmup.ends': '上限は{at}（あなたの時刻）までです。',
  'mail.warmup.paused': '今日の上限に達しました。{at}（あなたの時刻）から続けて送ります。',
  'mail.confirm.warmupOver': '一部は翌日以降に送られます。今日送れるのはあと{remaining}通で、超えた{over}通は翌日から自動で続けて送ります。',
  'mail.confirm.warmupLater': '送り始めて{days}日間は1日の上限があります。送る日に上限を超えた分は、翌日以降に自動で続けて送ります。',
  'mail.done.deferred': '{count}通は{at}（あなたの時刻）から自動で続けて送ります（送り始めの1日の上限のため）。',
  'mail.done.deferredNoTime': '{count}通は翌日に自動で続けて送ります（送り始めの1日の上限のため）。',
  'mail.history.carriedOver': '翌日に回った{count}通',
  'mail.detail.carriedOver': '送り始めの1日の上限で翌日に回った数: {count}通。{at}（あなたの時刻）から自動で続けて送ります。',
  // 2026-09-27 creator/mail-identity: 払い出しの本人確認が済んでいない間の上限（Core の mail-prelaunch）。期間の終わりが無いので「○日間は」と書かない。
  //   identityHint は Money の案の文言そのまま（受け取りの設定 = 出金のためのもの・お金は要らない、を必ず添える = 課金の誘導と誤解させない）
  'mail.warmup.identityTitle': '1日に送れる数の上限',
  'mail.warmup.identityBody': '1日に送れる数に上限があります（今日{limit}通・残り{remaining}通）。超えた分は翌日から自動で続けて送ります。',
  'mail.warmup.identityNotStarted': '1日に送れる数に上限があります（1日{limit}通）。超えた分は翌日から自動で続けて送ります。',
  'mail.warmup.identityHint': '1日に送れる数を増やすには、受け取りの設定（本人確認）を済ませてください。受け取りの設定は売上を出金するためのもので、お金を払わなくても済ませられます。',
  'mail.warmup.identityAction': '受け取りの登録を開く',
  'mail.confirm.identityLater': '1日に送れる数に上限があります。送る日に上限を超えた分は、翌日以降に自動で続けて送ります。',
  'mail.done.deferredIdentity': '{count}通は{at}（あなたの時刻）から自動で続けて送ります（1日の上限のため）。',
  'mail.done.deferredNoTimeIdentity': '{count}通は翌日に自動で続けて送ります（1日の上限のため）。',
  'mail.detail.carriedOverIdentity': '1日の上限で翌日に回った数: {count}通。{at}（あなたの時刻）から自動で続けて送ります。',

  'mail.history.title': '送信の履歴',
  'mail.history.refresh': '更新',
  'mail.history.empty': 'まだ送っていません',
  'mail.history.emptyBody': '送った・予約したメールと、その結果がここに出ます。',
  'mail.history.sentOf': '{recipients}人中{sent}通',
  'mail.status.draft': '下書き',
  'mail.status.scheduled': '予約',
  'mail.status.sending': '送信中',
  'mail.status.sent': '送信済み',
  'mail.status.canceled': '取り消し',
  'mail.status.failed': '失敗',
  'mail.detail.results': '結果',
  'mail.detail.recipients': '宛先',
  'mail.detail.sent': '送れた',
  'mail.detail.bounced': '宛先不明',
  'mail.detail.complained': '迷惑メールの報告',
  'mail.detail.unsubscribed': '配信停止',
  'mail.detail.failed': '送れなかった',
  'mail.detail.canceled': '送らなかった（取り消し・期限切れ）',
  'mail.detail.pending': '送信待ち',
  'mail.detail.lines': '直近の送信で、迷惑メールの報告が{complaint}以上、または宛先不明が{bounce}以上になると、すべてのプロジェクトのメールが自動で止まります。黄色はその半分に届いた目安です。',
  'mail.detail.recurrence': 'くり返し',
  'mail.detail.nextRun': '次の回',
  'mail.detail.sendAt': '予約の時刻',
  'mail.detail.sentAt': '送った時刻',
  'mail.detail.audience': '宛先',
  'mail.detail.filterLanguage': '言語: {lang}',
  'mail.detail.noFilter': '同意した全員',
  'mail.detail.createdAt': '作成',
  'mail.detail.localTime': '時刻はあなたのタイムゾーンで表示しています。',
  'mail.detail.sendDraft': '今すぐ送る',
  'mail.detail.sendDraftBody': 'この下書きを、今の宛先の条件に合う全員に送ります。届いたメールは取り消せません。',
  'mail.detail.cancel': '取り消す',
  'mail.detail.cancelTitle': 'このメールを取り消しますか？',
  'mail.detail.cancelBody': 'このメールは送られません。',
  'mail.detail.cancelRecurringBody': 'この定期のメールは、これ以降送られません。送った分はそのままです。',
  'mail.detail.stop': '送信を止める',
  'mail.detail.stopTitle': 'この送信を止めますか？',
  'mail.detail.stopBody': '届いたメールはそのままです。残りは送られません。',

  'mail.failure.sender_incomplete': '送る時点で差出人情報が揃っていませんでした。上で入れてから、もう一度送ってください。',
  'mail.failure.balance_due': '前払い残高が尽きていました。残高を足してから、もう一度送ってください。',
  'mail.failure.suspended': 'あなたのアカウントの送信が止まっていました（迷惑メールの報告か宛先不明が多いため）。',
  'mail.failure.feature_disabled': '送る時点で、このプロジェクトのメールがOFFでした。',
  'mail.failure.kids_audience': 'このプロジェクトの年齢層が "kids" のため、送れませんでした。',
  'mail.failure.provider_error': 'メールの送信サービスに断られました。時間をおいてもう一度送ってください。',
  'mail.failure.canceled': '取り消しました。これ以降は送られません。',
  'mail.failure.internal_error': 'こちら側で問題が起きました。もう一度送ってください。続くときはサポートに連絡してください。',
  'mail.failure.balance_due_recurring': '前払い残高が尽きたため、この定期の配信は取り消されました。入金しても再開しません。残高を足してから、作り直してください。',
  'mail.failure.project_deleted': 'プロジェクトを削除したため、これ以降は送られません。',
  'mail.failure.owner_changed': 'プロジェクトの持ち主が替わったため、この予約したメールを止めました。あなたの名前で送るには、作り直してください。',
  'mail.failure.account_closed': 'アカウントを閉鎖したため、これ以降は送られません。',

  'mail.error.balance_due': '前払い残高が無いため、メールを送れません。残高を足してからもう一度送ってください。',
  'mail.error.kids_audience': 'このプロジェクトの年齢層は "kids" です。メールは大人の利用者にだけ送れるため、送れません。',
  'mail.error.not_draft': '今すぐ送れるのは下書きだけです。このメールはもう送信・予約・取り消しのどれかになっています。履歴を更新してください。',
  'mail.error.not_cancelable': 'このメールはもう取り消せません（送り終えたか、取り消し済みです）。履歴を更新してください。',
  'mail.error.idempotency_key_reused': 'この送信は、別の内容ですでに受け付けられています。確認を閉じて、もう一度確かめてから送ってください。',
  'mail.error.mail_not_configured': 'この環境では、まだメールを送れません（{product}の運営側の設定待ちです）。あなたの設定に問題はありません。',
  'mail.error.body_too_large': 'メールが長すぎます。本文は 80,000 バイトまでです（日本語なら約 2 万 6 千字、英語なら約 8 万字）。これより長いと途中で切られ、必須のフッターが隠れてしまうためです。短くしてから、もう一度お試しください。',
  'error.sender_incomplete': '送る前に差出人情報（正式な氏名・住所・問い合わせ先）を入れてください。法律でメールの最後に必ず書くことになっています。',
  'error.mail_suspended': '迷惑メールの報告か宛先不明が多かったため、あなたのアカウントのメールの送信を止めています。再開には{product}の運営の確認が要ります。',

  // ---------------------------------------------------------------- Kumo からのお知らせの欄（KumoNewsCard）
  'kumoNews.title': '{product}からのお知らせの欄',
  'kumoNews.label': '「{product}からのお知らせを受け取る」の欄を出す',
  'kumoNews.body': 'メールアドレスの登録画面に、{product}自身のお知らせ（新しいアプリや機能のご案内）のチェックボックスがもう1つ出ます。選ぶのは利用者本人で、最初はチェックなし・確認メールで確定します。あなたに費用はかかりません。OFFにすると、このプロジェクトの登録画面には出ません。',
  'kumoNews.adultsNote': '大人の利用者にだけ出ます。子ども向けのプロジェクトには出ません。',
  'kumoNews.turnOff': '外す',
  'kumoNews.turnOn': '出す',
  'kumoNews.confirmOffTitle': '{env}で{product}からのお知らせの欄を外しますか？',
  'kumoNews.confirmOnTitle': '{env}で{product}からのお知らせの欄を出しますか？',
  'kumoNews.confirmOffBody': '{env}に新しいconfigを反映します。数秒で、このプロジェクトの登録画面に欄が出なくなります。すでに登録した利用者には、本人が止めるまで{product}のお知らせが届きます。',
  'kumoNews.confirmOnBody': '{env}に新しいconfigを反映します。数秒で、このプロジェクトの登録画面にまた欄が出ます（最初はチェックなし）。',
  'kumoNews.confirmOff': '{env}で外す',
  'kumoNews.confirmOn': '{env}で出す'
};
